import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./SHEventBulkTicketEntry.css";
import Services from "../../../../../services/Services";
import SheetFlexGrid from "../../../../../components/SheetFlexGrid/SheetFlexGrid";
import { useLanguage } from "../../../../../LanguageProvider";
import { GeneralContext } from "../../../../../GeneralProvider";
import AlertBox from "../../../../../components/AlertBox/AlertBox";
import * as XLSX from "xlsx";

export default function SHEventBulkTicketEntry({ event }) {
  const { setCreateProjectLoading } = useContext(GeneralContext);
  const { getTranslation } = useLanguage();
  const token = localStorage.getItem("token");

  // ----------------- STATE -----------------
  const [rows, setRows] = useState([{ name: "", lastname: "", email: "", ticketId: "" }]);
  const [soldEmails, setSoldEmails] = useState(new Set());
  const [loadingSold, setLoadingSold] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaultTicketId, setDefaultTicketId] = useState(event?.tickets?.[0]?.ticketID || "");
  const [defaultIsPaid, setDefaultIsPaid] = useState(true);
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [selectedColumn, setSelectedColumn] = useState();
  // 🔔 Alert
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState("success");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");

  // Excel import
  const fileInputRef = useRef(null);

  // Yapıştırma/odak anchor’u
  const [pasteAnchor, setPasteAnchor] = useState({ rowIndex: 0, colKey: "name" });

  // Grid container
  const gridContainerRef = useRef(null);

  // ----------------- HELPERS -----------------
  const normalizeSpace = (s) => String(s || "").replace(/\s+/g, " ").trim();

  const splitFullName = (full) => {
    const f = normalizeSpace(full);
    if (!f) return { name: "", lastname: "" };
    const parts = f.split(" ").filter(Boolean);
    if (parts.length === 1) return { name: parts[0], lastname: "" };
    const lastname = parts.pop(); // SON kelime soyisim
    const name = parts.join(" ");
    return { name, lastname };
  };

  const ticketOptions = useMemo(
    () =>
      (event?.tickets || []).map((t) => ({
        label: `${t.title} ${t.price ? `— ${t.price} ${t.currency}` : ""}`,
        value: t.ticketID,
      })),
    [event?.tickets]
  );

  const validTicketIds = useMemo(() => new Set(ticketOptions.map((o) => String(o.value))), [ticketOptions]);
  const labelToId = useMemo(() => {
    const m = new Map();
    ticketOptions.forEach((o) => m.set(o.label.toLowerCase(), String(o.value)));
    return m;
  }, [ticketOptions]);

  const resolveTicket = (raw) => {
    if (!raw) return "";
    const s = String(raw).trim();
    if (!s) return "";
    if (validTicketIds.has(s)) return s; // doğrudan ID
    const byLabel = labelToId.get(s.toLowerCase());
    if (byLabel) return byLabel;
    for (const [label, id] of labelToId.entries()) {
      if (label.startsWith(s.toLowerCase())) return id;
    }
    return "";
  };

  useEffect(() => {
    if (!token || !event?.eventID) return;
    let mounted = true;
    (async () => {
      setLoadingSold(true);
      try {
        const res = await Services.SocialHiveGetEventSoldTickets(token, event.eventID);
        const data = res?.data?.list || res?.data?.tickets || res?.data?.data || res?.data?.result || [];
        const emails = new Set(
          (Array.isArray(data) ? data : [])
            .map((x) => String(x.email || "").toLowerCase())
            .filter(Boolean)
        );
        if (mounted) setSoldEmails(emails);
      } finally {
        if (mounted) setLoadingSold(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token, event?.eventID]);

  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i, []);
  const alreadySold = (email) => (email ? soldEmails.has(String(email).toLowerCase()) : false);

  // ticketId ARTIK ZORUNLU DEĞİL
  const isValidRow = (row) => row.name?.trim() && row.lastname?.trim() && emailRegex.test(row.email || "");

  // ----------------- DOM & FOCUS YARDIMCILARI -----------------
  const COLUMN_ORDER = ["name", "lastname", "email", "ticketId"];
  const colIndex = (key) => Math.max(0, COLUMN_ORDER.indexOf(key));
  const clamp = (n, min = 0) => (Number.isFinite(n) ? Math.max(min, n) : min);

  // Hücre wrapper’ını (data-colkey taşıyan) yakala
  const closestCellWrapper = (el) => {
    if (!el) return null;
    return el.closest?.('[data-colkey], [data-colKey], [data-col-key]');
  };

  // Kolon anahtarını çöz (önce wrapper’dan)
  const findColKeyFromAnyEl = (el) => {
    if (!el) return null;
    const cell = closestCellWrapper(el);
    const dk =
      cell?.getAttribute?.("data-colkey") ||
      cell?.getAttribute?.("data-colKey") ||
      cell?.getAttribute?.("data-col-key");
    if (dk) return dk;

    // Input üzerindeki data-colkey varsa
    const dk2 =
      el.getAttribute?.("data-colkey") ||
      el.getAttribute?.("data-colKey") ||
      el.getAttribute?.("data-col-key");
    if (dk2) return dk2;

    // name → fallback
    const nm = el.getAttribute?.("name");
    if (nm === "sh-name") return "name";
    if (nm === "sh-lastname") return "lastname";
    if (nm === "sh-email") return "email";
    if (nm === "sh-ticketId") return "ticketId";

    // aria-label → fallback
    const aria = (el.getAttribute?.("aria-label") || "").toLowerCase();
    if (aria.includes("last") || aria.includes("soy")) return "lastname";
    if (aria.includes("mail")) return "email";
    if (aria.includes("ticket")) return "ticketId";
    if (aria.includes("ad") || aria.includes("name")) return "name";

    return null;
  };

  // Satır index’ini çöz (aynı colkey’e sahip hücre wrapper’ları arasında DOM index)
  const inferRowIndexFromAnyEl = (el, colKey) => {
    if (!el || !colKey) return null;
    const root = gridContainerRef.current || document;
    const cell = closestCellWrapper(el);
    if (!cell) {
      // Doğrudan select/input listesi dene (fallback)
      if (colKey === "ticketId") {
        const list = Array.from(root.querySelectorAll('select[name="sh-ticketId"]'));
        const idx = list.indexOf(el);
        return idx >= 0 ? idx : null;
      }
      const list = Array.from(root.querySelectorAll(`input[name="sh-${colKey}"]`));
      const idx = list.indexOf(el);
      return idx >= 0 ? idx : null;
    }
    // Wrapper listesi
    const wrappers = Array.from(
      root.querySelectorAll(`[data-colkey="${colKey}"], [data-colKey="${colKey}"], [data-col-key="${colKey}"]`)
    );
    const idx = wrappers.indexOf(cell);
    return idx >= 0 ? idx : null;
  };

  // Odaklanan gerçek form elemanını bul
  const getActiveFormControl = () => {
    const ae = document.activeElement;
    if (!ae) return null;
    if (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.tagName === "SELECT") return ae;
    const deeper = ae.querySelector?.("input, textarea, select");
    if (deeper) return deeper;
    return null;
  };

  // ---- Focus geldiğinde anchor’ı güncelle (capture ile, wrapper’dan çözüyoruz) ----
  useEffect(() => {
    const root = gridContainerRef.current;
    if (!root) return;

    const onFocusInCapture = (e) => {
      const target = e.target;
      const control =
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT")
          ? target
          : target.querySelector?.("input, textarea, select") || getActiveFormControl();

      const colKey = findColKeyFromAnyEl(control || target) || pasteAnchor.colKey || "name";
      const rowIdx =
        inferRowIndexFromAnyEl(control || target, colKey) ??
        pasteAnchor.rowIndex ??
        0;

      setPasteAnchor({ rowIndex: clamp(rowIdx, 0), colKey });
    };

    root.addEventListener("focusin", onFocusInCapture, true);
    return () => root.removeEventListener("focusin", onFocusInCapture, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, ticketOptions.length]);

  // ---- Add Row: yeni satıra odak + anchor ----
  const addEmptyRow = () => {
    setRows((r) => {
      const next = [...r, { name: "", lastname: "", email: "", ticketId: defaultTicketId }];
      queueMicrotask(() => {
        try {
          const root = gridContainerRef.current;
          const nameCells = root?.querySelectorAll?.('[data-colkey="name"], [data-colKey="name"], [data-col-key="name"]');
          const lastCell = nameCells && nameCells[nameCells.length - 1];
          const lastInput =
            lastCell?.querySelector?.("input") ||
            root?.querySelectorAll?.('input[name="sh-name"]')?.[nameCells.length - 1];

          if (lastInput) {
            lastInput.focus();
            setPasteAnchor({ rowIndex: next.length - 1, colKey: "name" });
          }
        } catch { }
      });
      return next;
    });
  };

  const clearAll = () => {
    setRows([{ name: "", lastname: "", email: "", ticketId: defaultTicketId }]);
    setShowRequiredErrors(false);
    setPasteAnchor({ rowIndex: 0, colKey: "name" });
  };

  const applyTicketToAll = () => setRows((r) => r.map((row) => ({ ...row, ticketId: defaultTicketId })));

  // ----------------- HEADER ALGILAMA -----------------
  const HEADER_PATTERNS = useMemo(() => {
    const or = (arr) => new RegExp(`^(?:${arr.join("|")})$`, "i");
    const NAME = or(["ad", "adı", "isim", "first ?name", "vorname", "prenom", "prénom", "nombre", "nome", "nome di battesimo"]);
    const LASTNAME = or(["soyad", "soyadı", "last ?name", "nachname", "familienname", "apellido", "apellidos", "cognome", "nom de famille"]);
    const FULLNAME = or(["ad soyad", "adı soyadı", "isim soyisim", "full ?name", "name", "nome e cognome", "nombre y apellido", "nombre completo", "nom complet"]);
    const EMAIL = or(["e-?posta", "eposta", "email", "e-mail", "mail", "correo", "correo electr[oó]nico", "mailadresse", "adresse ?e-?mail", "indirizzo ?e-?mail"]);
    const TICKET = or(["ticket ?id", "ticket", "bilet", "bilet id", "billet", "entrada", "biglietto", "id (?:biglietto|billet)", "no(?:\\.|)?", "numero ticket", "n.?° ticket", "ticket num(?:ber)?"]);
    return { NAME, LASTNAME, FULLNAME, EMAIL, TICKET };
  }, []);

  const detectHeaderMap = (headerRow = []) => {
    const normalize = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
    const map = { name: -1, lastname: -1, fullName: -1, email: -1, ticketId: -1 };
    headerRow.forEach((cell, idx) => {
      const h = normalize(cell);
      if (HEADER_PATTERNS.FULLNAME.test(h)) map.fullName = idx;
      else if (HEADER_PATTERNS.NAME.test(h)) map.name = idx;
      else if (HEADER_PATTERNS.LASTNAME.test(h)) map.lastname = idx;
      else if (HEADER_PATTERNS.EMAIL.test(h)) map.email = idx;
      else if (HEADER_PATTERNS.TICKET.test(h)) map.ticketId = idx;
    });
    const matchCount = Object.values(map).filter((v) => v >= 0).length;
    return matchCount >= 2 ? map : null;
  };

  const guessMapFromRow = (row = []) => {
    let emailIdx = row.findIndex((c) => emailRegex.test(String(c || "").trim()));
    if (emailIdx === -1) emailIdx = 2;
    let ticketIdx = -1;
    row.forEach((c, i) => {
      const s = String(c || "").trim();
      if (resolveTicket(s)) ticketIdx = i;
    });
    let nameIdx = -1, lastnameIdx = -1, fullNameIdx = -1;
    const first = normalizeSpace(row[0] || "");
    const second = normalizeSpace(row[1] || "");
    const hasSpaceFirst = first.includes(" ");
    const hasSpaceSecond = second.includes(" ");
    if (hasSpaceFirst && emailIdx !== 0) {
      fullNameIdx = 0;
    } else if (hasSpaceSecond && emailIdx !== 1) {
      fullNameIdx = 1;
    } else {
      if (row.length >= 2) {
        nameIdx = 0;
        if (emailIdx !== 1) lastnameIdx = 1;
      } else if (row.length === 1 && !emailRegex.test(first)) {
        fullNameIdx = 0;
      }
    }
    return { name: nameIdx, lastname: lastnameIdx, fullName: fullNameIdx, email: emailIdx, ticketId: ticketIdx };
  };

  // ----------------- PASTE YÖNETİMİ -----------------
  const mapNameAttrToCol = (nm) => {
    if (!nm) return null;
    if (nm === "sh-name") return "name";
    if (nm === "sh-lastname") return "lastname";
    if (nm === "sh-email") return "email";
    if (nm === "sh-ticketId") return "ticketId";
    return null;
  };

  // hedef gerçek input/select elementini bul
  const findTargetInputEl = (el) => {
    let cur = el;
    const stopEl = gridContainerRef.current || document.body;
    let hops = 0;
    while (cur && cur !== stopEl && hops < 10) {
      if (cur.tagName === "INPUT" || cur.tagName === "TEXTAREA" || cur.tagName === "SELECT") return cur;
      cur = cur.parentElement;
      hops++;
    }
    const ae = document.activeElement;
    if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.tagName === "SELECT")) return ae;
    const fromWrapper = el?.querySelector?.("input, textarea, select");
    if (fromWrapper) return fromWrapper;
    return null;
  };

  // ✅ Hedef hücreye odaklandığımız anda anchor’ı sabitle
  const setAnchorFromEvent = (colKey) => (e) => {
    const idx = inferRowIndexFromAnyEl(e.target, colKey);
    if (idx != null) {
      setPasteAnchor({ rowIndex: clamp(idx, 0), colKey });
    } else {
      // Son çare: aktif elemandan çöz
      const ctrl = getActiveFormControl();
      const fallbackIdx = inferRowIndexFromAnyEl(ctrl, colKey);
      if (fallbackIdx != null) setPasteAnchor({ rowIndex: clamp(fallbackIdx, 0), colKey });
      else setPasteAnchor((a) => ({ rowIndex: clamp(a.rowIndex ?? 0, 0), colKey }));
    }
  };

  // ---- Window-level paste’i kesin, biz yöneteceğiz ----
  const hardPasteIntercept = (e) => {
    const text = e.clipboardData?.getData("text") ?? "";
    if (!text) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

    // 🔑 ÖNCE anchor’ı kullan, DOM çözümlemeyi sadece yedek olarak dene
    const anchorColKey = pasteAnchor?.colKey || "name";
    const anchorRowIdx =
      Number.isFinite(pasteAnchor?.rowIndex) ? pasteAnchor.rowIndex : 0;

    // Yedek çözüm (gerekirse)
    let targetCol = anchorColKey;
    let targetRow = anchorRowIdx;

    const inputEl =
      findTargetInputEl(e.target) ||
      findTargetInputEl(document.activeElement);

    if (inputEl) {
      const maybeCol = findColKeyFromAnyEl(inputEl);
      if (!pasteAnchor?.colKey && maybeCol) targetCol = maybeCol;
      const maybeRow = inferRowIndexFromAnyEl(inputEl, targetCol);
      if (!Number.isFinite(pasteAnchor?.rowIndex) && Number.isFinite(maybeRow)) {
        targetRow = maybeRow;
      }
    }

    customPasteParser({
      text,
      startRowIndex: targetRow,
      startColKey: targetCol,
    });
  };

  useEffect(() => {
    const onCap = (e) => hardPasteIntercept(e);
    window.addEventListener("paste", onCap, { capture: true });
    return () => window.removeEventListener("paste", onCap, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasteAnchor, rows.length]);

  const handlePasteCapture = (e) => hardPasteIntercept(e);
  const inputPasteCapture = (e) => hardPasteIntercept(e);

  const assignRelativeCells = (cells, baseRow, startColKey) => {
    const start = colIndex(startColKey);
    const next = { ...baseRow };
    for (let i = 0; i < cells.length && start + i < COLUMN_ORDER.length; i++) {
      const targetKey = COLUMN_ORDER[start + i];
      let v = String(cells[i] ?? "").trim();
      if (!v) continue;

      const looksEmail = emailRegex.test(v);
      if (looksEmail) {
        next.email = normalizeSpace(v);
        continue;
      }

      if (targetKey === "ticketId") {
        const resolved = resolveTicket(v);
        if (resolved) next.ticketId = resolved;
        continue;
      }

      if (targetKey === "name") {
        v = normalizeSpace(v);
        if (v.includes(" ")) {
          const sp = splitFullName(v);
          next.name = sp.name;
          if (!next.lastname) next.lastname = sp.lastname || "";
        } else {
          next.name = v;
        }
        continue;
      }

      if (targetKey === "lastname") {
        v = normalizeSpace(v);
        if (v.includes(" ")) {
          const sp = splitFullName(v);
          if (!next.name) next.name = sp.name;
          next.lastname = sp.lastname || next.lastname || "";
        } else {
          next.lastname = v;
        }
        continue;
      }

      if (targetKey === "email") {
        next.email = normalizeSpace(v);
      }
    }
    return next;
  };

  const parsePastedText = (text) => {
    const lines = String(text || "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) return { rows: [], hasDelims: false };

    const out = [];
    let headerChecked = false;

    for (let li = 0; li < lines.length; li++) {
      const line = lines[li];
      const hasTab = /\t/.test(line);
      const hasSemicolon = /;/.test(line);
      const hasComma = /,/.test(line);
      const hasDelims = hasTab || hasSemicolon || hasComma;

      let cells = [line];
      if (hasDelims) {
        const sep = hasTab ? "\t" : hasSemicolon ? ";" : ",";
        cells = line.split(sep).map((c) => c.trim());
      }

      if (!headerChecked) {
        const headerMap = detectHeaderMap(cells);
        if (headerMap) {
          headerChecked = true;
          continue;
        }
        headerChecked = true;
      }

      let name = "", lastname = "", email = "", ticketId = "";

      if (cells.length === 1) {
        const v = normalizeSpace(cells[0]);
        if (emailRegex.test(v)) {
          email = v;
        } else {
          const sp = splitFullName(v);
          name = sp.name; lastname = sp.lastname;
        }
      } else if (cells.length === 2) {
        const a = normalizeSpace(cells[0]);
        const b = normalizeSpace(cells[1]);
        if (emailRegex.test(a) && !emailRegex.test(b)) {
          email = a;
          const sp = splitFullName(b);
          name = sp.name; lastname = sp.lastname;
        } else if (!emailRegex.test(a) && emailRegex.test(b)) {
          const sp = splitFullName(a);
          name = sp.name; lastname = sp.lastname;
          email = b;
        } else if (!emailRegex.test(a) && !emailRegex.test(b)) {
          const sp = splitFullName(a);
          name = sp.name; lastname = sp.lastname;
        } else {
          email = a;
        }
      } else {
        let emailIdx = cells.findIndex((c) => emailRegex.test(c));
        if (emailIdx === -1) emailIdx = 2;
        email = normalizeSpace(cells[emailIdx] || "");
        let tIdx = -1;
        for (let i = 0; i < cells.length; i++) {
          if (i === emailIdx) continue;
          const resolved = resolveTicket(cells[i]);
          if (resolved) { tIdx = i; ticketId = resolved; break; }
        }
        const candidates = cells
          .filter((_, i) => i !== emailIdx && i !== tIdx)
          .map(normalizeSpace)
          .filter(Boolean);
        if (candidates.length === 1) {
          const sp = splitFullName(candidates[0]);
          name = sp.name; lastname = sp.lastname;
        } else if (candidates.length >= 2) {
          name = candidates[0];
          lastname = candidates[candidates.length - 1];
        }
      }

      out.push({ cells, mapped: { name, lastname, email, ticketId }, hasDelims });
    }

    const anyDelims = out.some((r) => r.hasDelims);
    return { rows: out, hasDelims: anyDelims };
  };

  // ----------------- CUSTOM PASTE PARSER -----------------
  const customPasteParser = ({ text, startRowIndex, startColKey }) => {
    const { rows: parsedRows, hasDelims } = parsePastedText(text);
    const anchorRow = clamp(startRowIndex, 0);
    const anchorCol = startColKey || "name";

    if (parsedRows.length === 0) return { handled: false };

    // TEK HÜCRE → bulunduğun hücreye yaz (format şartı yok)
    if (parsedRows.length === 1) {
      const { cells } = parsedRows[0];
      if (cells.length === 1) {
        const value = String(cells[0] || "").trim();
        const patch = [...rows];
        const base = patch[anchorRow] || { name: "", lastname: "", email: "", ticketId: "" };

        if (anchorCol === "name") {
          if (emailRegex.test(value)) {
            patch[anchorRow] = { ...base, email: normalizeSpace(value) };
          } else if (value.includes(" ")) {
            const sp = splitFullName(value);
            patch[anchorRow] = { ...base, name: sp.name, lastname: sp.lastname || base.lastname };
          } else {
            patch[anchorRow] = { ...base, name: normalizeSpace(value) };
          }
        } else if (anchorCol === "lastname") {
          if (emailRegex.test(value)) {
            patch[anchorRow] = { ...base, email: normalizeSpace(value) };
          } else if (value.includes(" ")) {
            const sp = splitFullName(value);
            patch[anchorRow] = { ...base, name: base.name || sp.name, lastname: sp.lastname || "" };
          } else {
            patch[anchorRow] = { ...base, lastname: normalizeSpace(value) };
          }
        } else if (anchorCol === "email") {
          patch[anchorRow] = { ...base, email: normalizeSpace(value) };
        } else if (anchorCol === "ticketId") {
          const t = resolveTicket(value);
          if (t) patch[anchorRow] = { ...base, ticketId: t };
        }

        setRows(patch);
        return { handled: true };
      }
    }

    // Çoklu satır/hücre
    const patch = [...rows];
    let rIdx = anchorRow;

    for (let i = 0; i < parsedRows.length; i++) {
      const { cells, mapped } = parsedRows[i];
      const base = patch[rIdx] || { name: "", lastname: "", email: "", ticketId: "" };

      let next;
      if (hasDelims && cells.length >= 1) {
        next = assignRelativeCells(cells, base, anchorCol);
      } else {
        next = {
          ...base,
          ...(mapped.name ? { name: mapped.name } : {}),
          ...(mapped.lastname ? { lastname: mapped.lastname } : {}),
          ...(mapped.email ? { email: mapped.email } : {}),
          ...(mapped.ticketId ? { ticketId: mapped.ticketId } : {}),
        };
      }

      patch[rIdx] = next;
      rIdx += 1;
    }

    setRows(patch);
    return { handled: true };
  };

  // ----------------- EXCEL/CSV IMPORT -----------------
  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
      if (!matrix || matrix.length === 0) return;

      let body = matrix;
      let map = null;

      const headerMap = detectHeaderMap(matrix[0]);
      if (headerMap) {
        body = matrix.slice(1);
        map = headerMap;
      } else {
        const probe = matrix.find((r) => (r || []).some((c) => String(c || "").trim().length > 0)) || [];
        map = guessMapFromRow(probe);
      }

      const imported = [];
      for (const r of body) {
        const get = (i) => (i >= 0 ? r[i] : "");
        let name = "", lastname = "", email = "", ticketId = "";

        const rawFull = get(map.fullName);
        const rawName = get(map.name);
        const rawLastname = get(map.lastname);
        const rawEmail = get(map.email);
        const rawTicket = get(map.ticketId);

        if (rawEmail) email = normalizeSpace(rawEmail);

        if (rawFull) {
          const sp = splitFullName(rawFull);
          name = sp.name; lastname = sp.lastname;
        } else {
          const n = normalizeSpace(rawName);
          const l = normalizeSpace(rawLastname);
          if (n && !l && n.includes(" ")) {
            const sp = splitFullName(n);
            name = sp.name; lastname = sp.lastname;
          } else {
            name = n; lastname = l;
          }
        }

        ticketId = resolveTicket(rawTicket);

        if (name || lastname || email || ticketId) {
          imported.push({ name, lastname, email, ticketId });
        }
      }

      if (imported.length === 0) return;

      const anchorRow = Number.isFinite(pasteAnchor.rowIndex) ? Math.max(0, pasteAnchor.rowIndex) : 0;

      setRows((prev) => {
        const patch = [...prev];
        let rIdx = anchorRow;
        for (let i = 0; i < imported.length; i++) {
          const p = imported[i];
          const base = patch[rIdx] || { name: "", lastname: "", email: "", ticketId: "" };
          patch[rIdx] = {
            ...base,
            ...(p.name ? { name: p.name } : {}),
            ...(p.lastname ? { lastname: p.lastname } : {}),
            ...(p.email ? { email: p.email } : {}),
            ...(p.ticketId ? { ticketId: p.ticketId } : {}),
          };
          rIdx += 1;
        }
        return patch;
      });
    } finally {
      e.target.value = "";
    }
  };

  const saveAll = async () => {
    setShowRequiredErrors(true);
    setCreateProjectLoading(true);
    setSaving(true);
    try {
      const results = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!isValidRow(row)) {
          results.push({ status: "invalid" });
          continue;
        }
        try {
          const billing = { tckn: "", address: "", city: "", region: "", postalCode: "" };
          const res = await Services.SocialHiveCreateSoldTicket(
            token,
            event.channelID,
            event.eventID,
            row.ticketId || "",
            "bulk-entry",
            row.name,
            row.lastname,
            row.email,
            "1111111111",
            !!defaultIsPaid,
            billing
          );
          results.push({ status: "fulfilled", data: res?.data });
        } catch (err) {
          results.push({ status: "rejected", reason: err });
        }
      }

      const processed = rows.map((row, idx) => ({ ...row, __status: results[idx]?.status }));
      const remaining = processed.filter((r) => r.__status !== "fulfilled");

      const nextRows =
        remaining.length > 0 ? remaining : [{ name: "", lastname: "", email: "", ticketId: defaultTicketId }];

      setRows(nextRows);

      const newSold = new Set(soldEmails);
      rows.forEach((row, idx) => {
        if (results[idx]?.status === "fulfilled" && row.email) {
          newSold.add(String(row.email).toLowerCase());
        }
      });
      setSoldEmails(newSold);

      const successCount = results.filter((r) => r.status === "fulfilled").length;
      const invalidCount = results.filter((r) => r.status === "invalid").length;
      const rejectedCount = results.filter((r) => r.status === "rejected").length;
      const failCount = invalidCount + rejectedCount;

      if (successCount > 0 && failCount === 0) {
        setAlertType("success");
        setAlertTitle(getTranslation("success"));
        setAlertMessage(getTranslation("ticketsAddedSuccess", { successCount }));
        setAlertOpen(true);
      } else if (successCount > 0 && failCount > 0) {
        setAlertType("warning");
        setAlertTitle(getTranslation("warning"));
        setAlertMessage(
          getTranslation("ticketsAddedPartial", { successCount: successCount, failCount: failCount })
        );
        setAlertOpen(true);
      } else {
        setAlertType("error");
        setAlertTitle(getTranslation("error"));
        setAlertMessage(getTranslation("ticketsAddError"));
        setAlertOpen(true);
      }
    } finally {
      setCreateProjectLoading(false);
      setSaving(false);
    }
  };

  // ----------------- GRID KOLONLARI -----------------
  const columns = useMemo(
    () => [
      {
        key: "name",
        title: getTranslation("name"),
        type: "text",
        placeholder: getTranslation("name"),
        required: true,
        extraProps: {
          autoComplete: "new-password",
          name: "sh-name",
          "data-colkey": "name",
          "aria-label": "name",
          onPasteCapture: inputPasteCapture,
          onFocus: setAnchorFromEvent("name"),
          onClick: setAnchorFromEvent("name"),
        },
        cellProps: { "data-colkey": "name" },
      },
      {
        key: "lastname",
        title: getTranslation("lastname"),
        type: "text",
        placeholder: getTranslation("lastname"),
        required: true,
        extraProps: {
          autoComplete: "new-password",
          name: "sh-lastname",
          "data-colkey": "lastname",
          "aria-label": "lastname",
          onPasteCapture: inputPasteCapture,
          onFocus: setAnchorFromEvent("lastname"),
          onClick: setAnchorFromEvent("lastname"),
        },
        cellProps: { "data-colkey": "lastname" },
      },
      {
        key: "email",
        title: getTranslation("email"),
        type: "text",
        placeholder: "email@example.com",
        required: true,
        extraProps: {
          autoComplete: "new-password",
          name: "sh-email",
          "data-colkey": "email",
          "aria-label": "email",
          onPasteCapture: inputPasteCapture,
          onFocus: setAnchorFromEvent("email"),
          onClick: setAnchorFromEvent("email"),
        },
        cellProps: { "data-colkey": "email" },
        validate: (v) => !v || emailRegex.test(v || ""),
        tooltip: (v) =>
          alreadySold(v)
            ? getTranslation("emailExist")
            : !emailRegex.test(v || "") && (v || "").trim().length > 0
              ? getTranslation("emailError.")
              : "",
      },
      {
        key: "ticketId",
        title: getTranslation("ticketType"),
        type: "select",
        placeholder: getTranslation("select"),
        options: ticketOptions,
        required: false,
        extraProps: {
          name: "sh-ticketId",
          "data-colkey": "ticketId",
          "aria-label": "ticketId",
          onPasteCapture: inputPasteCapture,
          onFocus: setAnchorFromEvent("ticketId"),
          onClick: setAnchorFromEvent("ticketId"),
        },
        cellProps: { "data-colkey": "ticketId" },
      },
    ],
    [ticketOptions, emailRegex, soldEmails, getTranslation]
  );

  const footerLeft = (
    <div>
      {getTranslation("totalRow")}: <b>{rows.length}</b>
    </div>
  );
  const legend = [
    { dotClass: "required-dot", label: getTranslation("missingRequiredField") },
    { dotClass: "invalid-email-dot", label: getTranslation("invalidEmail") },
    { dotClass: "sold-dot", label: getTranslation("emailAlreadyTaken") },
  ];

  return (
    <div
      className="sh-settings-main"
      ref={gridContainerRef}
      onPasteCapture={handlePasteCapture}
    >
      <div className="sh-bulk-toolbar">
        <div className="toolbar-left">
          <label className="toolbar-label">{getTranslation("ticketType")}</label>
          <select
            className="toolbar-select"
            value={defaultTicketId}
            onChange={(e) => setDefaultTicketId(e.target.value)}
          >
            {ticketOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button className="toolbar-button" onClick={applyTicketToAll}>
            {getTranslation("applyToAll")}
          </button>
        </div>
        <div className="toolbar-right">
          <label className="toggle">
            <input
              type="checkbox"
              checked={defaultIsPaid}
              onChange={(e) => setDefaultIsPaid(e.target.checked)}
            />
            <span>{getTranslation("paymentDone")}</span>
          </label>

          {/* Excel Import Button */}
          <button className="toolbar-button" onClick={() => fileInputRef.current?.click()}>
            {getTranslation("importFromExcel") || "Excel'den İçe Aktar"}
          </button>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleExcelImport}
          />

          <button className="toolbar-button" onClick={addEmptyRow}>
            {getTranslation("addRow")}
          </button>
          <button className="toolbar-button" onClick={clearAll}>
            {getTranslation("clear")}
          </button>
          <button className="toolbar-button primary" onClick={saveAll} disabled={saving}>
            {getTranslation("save")}
          </button>
        </div>
      </div>

      <SheetFlexGrid
        rows={rows}
        onChange={setRows}
        columns={columns}
        includeIndex
        includeDelete
        statusKey="__status"
        statusLabels={{
          fulfilled: getTranslation("success"),
          rejected: getTranslation("error"),
          invalid: getTranslation("missing"),
        }}
        statusClasses={{ fulfilled: "ok", rejected: "err", invalid: "warn" }}
        footerLeft={footerLeft}
        legend={legend}
        pasteEnabled={false}
        resolveOption={(col, raw) => resolveTicket(raw)}
        onCellFocus={(rowIndex, colKey) =>
          setPasteAnchor({
            rowIndex: clamp(rowIndex, 0),
            colKey: colKey || "name",
          })
        }
        pasteStartsAtAnchor
        showRequiredErrors={showRequiredErrors}
        extraCellClass={(row, col, val) => {
          if (col.key === "email" && alreadySold(val)) return "already-sold";
          return "";
        }}
      />

      <AlertBox
        isOpen={alertOpen}
        type={alertType}
        title={alertTitle}
        message={alertMessage}
        confirmText={getTranslation("ok")}
        onConfirm={() => setAlertOpen(false)}
      />
    </div>
  );
}